import { Injectable } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { DynamicFirebaseService } from './dynamic-firebase.service';
import { Cargo } from '../interfaces/cargo';
import { CargoPaymentDetail } from '../interfaces/cargoPayment';
import { DateManager } from '../managers/date.manager';
import { Utils } from '../resources/utils';
import { ApprovalStateEnum, TravelExpense, TravelExpenseDetail } from '../interfaces/travel-expense';
import { TravelExpensesService } from './travel-expenses.service';
import { CargoTaxesPipe } from '../pipe/cargo-taxes.pipe';
import { AuthService } from './authentication.service';
import { environment } from 'src/environments/environment';
import { DiscountsAndBonuses } from '../interfaces/discountsAndBonuses';
import { SettingId } from '../interfaces/saas-config';

/**
 * Centralizes the logic for fetching, merging and normalizing cargo payments
 * from both the legacy `shippingCost.payments` field and the new Firestore subcollection.
 *
 * Usage pattern:
 *   1. Subscribe to `watchCargoPayments$(cargoId)` to get realtime CONFIRMED payments.
 *   2. Store emitted payments in a local `realtimePayments` array.
 *   3. Call `getCargoWithMergedPayments(cargo, realtimePayments)` to obtain a cargo
 *      whose `shippingCost.payments` contains the merged list.
 *   4. Pass the merged cargo to `CargoItemService` methods (getTotalValueBalance,
 *      getCargoBalancePaid, getSummaryBalance, etc.) which read from that field.
 */
@Injectable({ providedIn: 'root' })
export class CargoPaymentsService {

  private readonly SPANISH_MONTHS: { [key: string]: number } = {
    enero: 0, febrero: 1, marzo: 2, abril: 3, mayo: 4, junio: 5,
    julio: 6, agosto: 7, septiembre: 8, octubre: 9, noviembre: 10, diciembre: 11
  };

  constructor(
    private dynamicFirebaseService: DynamicFirebaseService,
    private utils: Utils,
    private travelExpensesService: TravelExpensesService,
    private cargoTaxesPipe: CargoTaxesPipe,
    private authService: AuthService
  ) { }

  /**
   * Returns a realtime observable of CONFIRMED payments from the cargo subcollection.
   * Emits a new array each time the subcollection changes.
   */
  watchCargoPayments$(cargoId: string): Observable<CargoPaymentDetail[]> {
    return this.dynamicFirebaseService.watchSubcollection$<any>({
      collection: 'Cargo',
      documentId: cargoId,
      subcollection: 'Payments'
    }).pipe(
      map(payments => this.sanitizeRealtimePayments(payments))
    );
  }

  /**
   * Filters CONFIRMED payments, normalizes dates, and sorts by date ascending.
   */
  sanitizeRealtimePayments(payments: any[]): CargoPaymentDetail[] {
    return this.sanitizePaymentsByStatus(payments, 'CONFIRMED');
  }

  /**
   * Returns a realtime observable that groups payments by the given statuses
   * from the cargo subcollection in a single snapshot listener. Intended for
   * read-only display in the payment history dialog — never merge with legacy
   * payments or `shippingCost.payments`.
   *
   * The result is an object keyed by every status in `statuses` (empty array
   * if no payments match), with payments normalized and sorted by date asc.
   */
  watchCargoPaymentsGrouped$(
    cargoId: string,
    statuses: string[]
  ): Observable<{ [status: string]: CargoPaymentDetail[] }> {
    return this.dynamicFirebaseService.watchSubcollection$<any>({
      collection: 'Cargo',
      documentId: cargoId,
      subcollection: 'Payments'
    }).pipe(
      map(payments => this.groupPaymentsByStatus(payments, statuses))
    );
  }

  private groupPaymentsByStatus(
    payments: any[],
    statuses: string[]
  ): { [status: string]: CargoPaymentDetail[] } {
    const grouped: { [status: string]: CargoPaymentDetail[] } = {};
    for (const status of statuses) grouped[status] = [];
    if (!Array.isArray(payments)) return grouped;
    for (const p of payments) {
      if (!p || !statuses.includes(p.status)) continue;
      grouped[p.status].push({ ...p, date: this.normalizeDate(p.date) });
    }
    for (const status of statuses) {
      grouped[status].sort((a, b) => this.getTime(a.date) - this.getTime(b.date));
    }
    return grouped;
  }

  private sanitizePaymentsByStatus(payments: any[], status: string): CargoPaymentDetail[] {
    if (!Array.isArray(payments)) return [];
    return payments
      .filter((p: any) => !!p && p.status === status)
      .map((p: CargoPaymentDetail) => ({ ...p, date: this.normalizeDate(p.date) }))
      .sort((a, b) => this.getTime(a.date) - this.getTime(b.date));
  }

  /**
   * Extracts and normalizes the legacy payments stored in `cargo.shippingCost.payments`.
   */
  getLegacyPayments(cargo: Cargo): CargoPaymentDetail[] {
    if (!cargo || !cargo.shippingCost || !Array.isArray(cargo.shippingCost.payments)) return [];
    return cargo.shippingCost.payments
      .filter(p => !!p)
      .map(p => ({ ...p, date: this.normalizeDate(p.date) }));
  }

  /**
   * Clave de respaldo para deduplicar un pago cuando no se puede confiar en su id
   * (los pagos embebidos en `shippingCost.payments` pueden no tenerlo).
   */
  private buildPaymentCompositeKey(payment: CargoPaymentDetail): string {
    const type = payment && payment.type ? payment.type : 'NO_TYPE';
    const amount = payment && this.utils.isDefined(payment.amount) ? payment.amount : 'NO_AMOUNT';
    return type + '|' + this.getTime(payment ? payment.date : null) + '|' + amount;
  }

  /**
   * Merges legacy and realtime payments into a single list sorted by date ascending.
   *
   * La lista legacy ya puede traer el mismo pago repetido: el detalle del servicio lo
   * arma concatenando la subcolección `Cargo/{id}/Payments` con `shippingCost.payments`,
   * así que aquí se deduplica la lista completa (no solo los realtime contra los legacy).
   * Esto evita tanto las filas repetidas en el histórico como el doble conteo del pago
   * en el saldo, que se calcula sobre esta misma lista.
   */
  mergePayments(legacyPayments: CargoPaymentDetail[], realtimePayments: CargoPaymentDetail[]): CargoPaymentDetail[] {
    const merged: CargoPaymentDetail[] = [];
    const indexById: { [id: string]: number } = {};
    const indexByCompositeKey: { [key: string]: number } = {};
    const hasKey = (index: { [key: string]: number }, key: string): boolean =>
      Object.prototype.hasOwnProperty.call(index, key);

    const addPayment = (payment: CargoPaymentDetail) => {
      if (!payment) return;
      const id = this.utils.isDefined(payment.id) ? String(payment.id) : null;
      const compositeKey = this.buildPaymentCompositeKey(payment);
      // Misma copia del pago (subcolección + array embebido comparten el id).
      if (id && hasKey(indexById, id)) return;
      if (hasKey(indexByCompositeKey, compositeKey)) {
        const position = indexByCompositeKey[compositeKey];
        const existing = merged[position];
        const existingId = this.utils.isDefined(existing.id) ? String(existing.id) : null;
        // Solo se colapsan cuando alguna de las dos copias no tiene id. Dos pagos con ids
        // distintos se consideran pagos distintos aunque coincidan tipo, fecha y valor.
        if (!existingId || !id) {
          // Conserva la copia con id: es la única sobre la que se puede eliminar el pago.
          if (!existingId && id) {
            merged[position] = payment;
            indexById[id] = position;
          }
          return;
        }
      }
      if (id) indexById[id] = merged.length;
      if (!hasKey(indexByCompositeKey, compositeKey)) indexByCompositeKey[compositeKey] = merged.length;
      merged.push(payment);
    };

    (Array.isArray(legacyPayments) ? legacyPayments : []).forEach(addPayment);
    (Array.isArray(realtimePayments) ? realtimePayments : []).forEach(addPayment);
    return merged.sort((a, b) => this.getTime(a.date) - this.getTime(b.date));
  }

  /**
   * Returns a shallow copy of `cargo` where `shippingCost.payments` is replaced
   * by the merged list of legacy + realtime payments.
   * All `CargoItemService` balance methods work correctly when given this cargo.
   */
  getCargoWithMergedPayments(cargo: Cargo, realtimePayments: CargoPaymentDetail[]): Cargo {
    if (!cargo || !cargo.shippingCost) return cargo;
    const legacy = this.getLegacyPayments(cargo);
    const merged = this.mergePayments(legacy, realtimePayments);
    return {
      ...cargo,
      shippingCost: { ...cargo.shippingCost, payments: merged }
    } as Cargo;
  }

  normalizeDate(date: any): string {
    const parsed = this.parseDate(date);
    if (parsed) return DateManager.dateToString(parsed, 'YYYY/MM/DD hh:mm a');
    return date;
  }

  getTime(date: any): number {
    const parsed = this.parseDate(date);
    return parsed ? parsed.getTime() : 0;
  }

  parseDate(date: any): Date | null {
    const ts = Utils.firebaseTimestampToDate(date);
    if (ts) return ts;
    if (date instanceof Date && !isNaN(date.getTime())) return date;
    if (typeof date !== 'string' || !date.trim()) return null;

    const simple = new Date(date);
    if (!isNaN(simple.getTime())) return simple;

    // Spanish-locale format: "15 de enero de 2024 a las 10:30:00 p.m. utc-5"
    const normalized = date.toLowerCase().replace(/\s+/g, ' ').trim();
    const regex = /^(\d{1,2}) de ([a-záéíóúñ]+) de (\d{4}) a las (\d{1,2}):(\d{2}):(\d{2})\s*(a\.?\s*m\.?|p\.?\s*m\.?)\s*utc([+-]\d{1,2})$/i;
    const m = normalized.match(regex);
    if (!m) return null;

    const day = Number(m[1]);
    const month = this.SPANISH_MONTHS[m[2]];
    const year = Number(m[3]);
    let hour = Number(m[4]);
    const minute = Number(m[5]);
    const second = Number(m[6]);
    const meridian = m[7].replace(/\s|\./g, '').toLowerCase();
    const utcOffset = Number(m[8]);

    if (month === undefined || [day, year, hour, minute, second, utcOffset].some(isNaN)) return null;
    if (meridian === 'pm' && hour < 12) hour += 12;
    if (meridian === 'am' && hour === 12) hour = 0;

    const utcMs = Date.UTC(year, month, day, hour, minute, second) - utcOffset * 3600000;
    const result = new Date(utcMs);
    return isNaN(result.getTime()) ? null : result;
  }

  private async getRealTimePayments(cargoId: string): Promise<CargoPaymentDetail[]> {
    try {
      const result: CargoPaymentDetail[] = await this.watchCargoPayments$(cargoId).pipe(take(1)).toPromise();
      return result && result.length ? result : [];
    } catch (error) {
      return [];
    }
  }

  async getBalance(cargo: Cargo, arePaymentsMerged: boolean, travelExpenses?: TravelExpense[]) {
    let newCargo = this.utils.clone(cargo);
    if (!arePaymentsMerged) {
      const realTimePayments = await this.getRealTimePayments(cargo.id)
      newCargo = this.getCargoWithMergedPayments(cargo, realTimePayments)
    }
    if (!travelExpenses && this.authService.hasCompanySettingEnabled(SettingId.USE_TRAVEL_EXPENSES)) {
      try {
        const result: TravelExpense[] = await this.travelExpensesService.byCargoId(cargo.id).toPromise();
        travelExpenses = result && result.length ? result : [];
      } catch (error) {
        travelExpenses = [];
      }
    }
    const travelExpensesBalance = this.getTravelExpensesBalance(travelExpenses, newCargo);
    const balance = this.getTotalValueBalance(newCargo) - travelExpensesBalance;
    return balance;
  }

  public getTravelExpensesBalance(travelExpenses: TravelExpense[], cargo: Cargo): number {
    if (!travelExpenses || !travelExpenses.length)
      return 0;
    const modifications = this.utils.getNestedValue(cargo, 'shippingCost.modifications');
    const hasTravelExpensesModifications = modifications && modifications.length && modifications.some(modification =>
      modification && modification.typeModification && modification.typeModification.name &&
      ["Bono por legalización de viáticos", "Descuento por legalización de viáticos"].includes(modification.typeModification.name));
    if (hasTravelExpensesModifications) return 0;

    const totalPaid = travelExpenses
      .filter(travelExpense => travelExpense.paid)
      .reduce((acc, travelExpense: TravelExpense) => acc + travelExpense.totalPaid, 0);

    const totalValidSpent = travelExpenses
      .map(travelExpenses => travelExpenses.travelExpensesDetail)
      .reduce((acc, travelExpenseDetails: Array<TravelExpenseDetail>) => acc.concat(travelExpenseDetails), [])
      .filter(travelExpenseDetail => travelExpenseDetail.approval === ApprovalStateEnum.APPROVED)
      .reduce((acc, travelExpenseDetail) => acc + travelExpenseDetail.value, 0);

    return totalPaid - totalValidSpent;
  }

  getTotalValueBalance(cargo: Cargo) {
    let value = 0;
    if (cargo && cargo.shippingCost) {
      let retefteValue = this.cargoTaxesPipe.transform(cargo.shippingCost.retefteValue, this.isChargeTaxes(cargo));
      let reteicaValue = this.cargoTaxesPipe.transform(cargo.shippingCost.reteicaValue, this.isChargeTaxes(cargo));
      let fopatValue = this.cargoTaxesPipe.transform(cargo.shippingCost.fopatValue, this.isChargeFopat(cargo));
      let taxes = retefteValue + reteicaValue + fopatValue;
      if (this.isTeclogiUser()) taxes += cargo.shippingCost.integralValue;
      let modificationValue = 0;
      modificationValue += this.getTotalBonuses(cargo);
      modificationValue -= this.getTotalDiscounts(cargo);
      const totalCost = cargo.shippingCost.totalCost + modificationValue;
      let totalPaid = 0;
      if (cargo.shippingCost.payments)
        cargo.shippingCost.payments.forEach((payment) => {
          if (payment.type === "advance" || payment.type === "extraAdvance" || payment.type === "balance") {
            totalPaid += payment.amount;
          }
        });
      value = (totalCost - (totalPaid + taxes));
    }
    return value;
  }

  public isChargeTaxes(cargo: Cargo): boolean {
    return !this.utils.isEmpty(cargo.chargeTaxes) ? cargo.chargeTaxes : true;
  }
  public isChargeFopat(cargo: Cargo): boolean {
    return this.utils.isTransportService(cargo);
  }

  getTotalBonuses(cargo: Cargo): number {
    if (cargo && cargo.shippingCost && cargo.shippingCost.modifications) {
      const discounts = cargo.shippingCost.modifications.filter(
        (modification: DiscountsAndBonuses) => {
          return modification.type == 0;
        }
      );
      if (discounts.length) {
        const total = discounts.reduce((a, b) => {
          return { amount: a.amount + b.amount };
        });
        return total.amount;
      }
    }
    return 0;
  }

  isShippingCostZero(cargo?: Cargo): boolean {
    if (!cargo || !cargo.shippingCost) return false;
    const { shippingCost } = cargo;
    // El flete efectivo incluye las modificaciones (e.g. "Ajuste a Flete"), que no se reflejan en freightCost/totalCost
    const modificationsValue = this.getTotalBonuses(cargo) - this.getTotalDiscounts(cargo);
    const realFreightCost = (shippingCost.freightCost || 0) + modificationsValue;
    const realTotalCost = (shippingCost.totalCost || 0) + modificationsValue;
    return shippingCost.rate === 0 || realFreightCost === 0 || realTotalCost === 0;
  }

  getTotalDiscounts(cargo: Cargo): number {
    if (cargo && cargo.shippingCost && cargo.shippingCost.modifications) {
      const discounts = cargo.shippingCost.modifications.filter(
        (modification: DiscountsAndBonuses) => {
          return modification.type == 1;
        }
      );
      if (discounts.length) {
        const total = discounts.reduce((a, b) => {
          return { amount: a.amount + b.amount };
        });
        return total.amount;
      }
    }
    return 0;
  }

  // Getters
  isTeclogiUser(): boolean {
    return !!(this.authService && this.authService.getCompany() && this.authService.getCompany().companyId === environment.rootNit);
  }

}
